import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Stack, Typography, CircularProgress } from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ApiService from "../../services/api-service";
import { getUserId } from "../../constants/function";
import notifier from "../../components/Notifier";
import GalleryGrid from "./GalleryGrid";
import GalleryUploadDialog from "./GalleryUploadDialog";
import { uploadGalleryPost } from "./galleryUpload";

export default function Gallery() {
  const { id: routeUserId } = useParams();
  const currentUserId = getUserId();
  const userId = routeUserId || currentUserId;
  const isOwner = String(userId) === String(currentUserId);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await ApiService.GALLERY_BY_USER(userId);
      const payload = (res && (res.result || res.data || res)) || {};
      setItems(payload.items || []);
    } catch (e) {
      notifier.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setPendingFiles(files);
    setShowUploadDialog(true);
  };

  const doUpload = async (caption) => {
    setUploading(true);
    try {
      // All selected files become a single multi-image post.
      const doc = await uploadGalleryPost(pendingFiles, { visibility: "public", caption });
      if (doc) {
        setItems((prev) => [doc, ...prev]);
        const n = pendingFiles.length;
        notifier.success(n > 1 ? `Posted ${n} images` : "Image posted");
      }
      setShowUploadDialog(false);
      setPendingFiles([]);
    } catch (err) {
      notifier.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const cancelUpload = () => {
    if (uploading) return;
    setShowUploadDialog(false);
    setPendingFiles([]);
  };

  const handleToggleVisibility = async (item) => {
    const next = item.visibility === "private" ? "public" : "private";
    const prevItems = items;
    setItems((arr) => arr.map((x) => (x._id === item._id ? { ...x, visibility: next } : x)));
    try {
      await ApiService.GALLERY_UPDATE(item._id, { visibility: next });
    } catch (e) {
      setItems(prevItems);
      notifier.error("Failed to update visibility");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm("Delete this image?")) return;
    const prevItems = items;
    setItems((arr) => arr.filter((x) => x._id !== item._id));
    try {
      await ApiService.GALLERY_DELETE(item._id);
    } catch (e) {
      setItems(prevItems);
      notifier.error("Failed to delete");
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>
          {isOwner ? "My Gallery" : "Gallery"}
        </Typography>

        {isOwner && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={handleFiles}
              style={{ display: "none" }}
            />
            <Button
              variant="contained"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={uploading}
              startIcon={
                uploading ? (
                  <CircularProgress size={16} sx={{ color: "#000" }} />
                ) : (
                  <AddPhotoAlternateOutlinedIcon />
                )
              }
              sx={{
                background: "#FFEE20",
                color: "#000",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                "&:hover": { background: "#FFE066" },
              }}
            >
              {uploading ? "Uploading…" : "Add images"}
            </Button>
          </>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <CircularProgress size={28} sx={{ color: "#FFEE20" }} />
        </Box>
      ) : (
        <GalleryGrid
          items={items}
          isOwner={isOwner}
          onToggleVisibility={handleToggleVisibility}
          onDelete={handleDelete}
        />
      )}

      <GalleryUploadDialog
        open={showUploadDialog}
        files={pendingFiles}
        uploading={uploading}
        onCancel={cancelUpload}
        onConfirm={doUpload}
      />
    </Box>
  );
}
